import { useEffect, useState } from "react";
import { CardContent, Card, Typography, Button } from "@mui/material";
import { Box, Stack } from "@mui/system";
import Logo from "assets/icons/logo_light_horisontal.png";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import { requiredRules } from "shared/helpers/validators";
import TextInput from "shared/ui/TextInput";
import { ERequestStatus } from "store/enums/index.enum";
import { ResetForgotPassword } from "store/slicers/users";

interface IFormValues {
    newPassword: string;
    confirmPassword: string;
}

const NewPasswordPage = () => {
    const dispatch = useAsyncDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    const code = location.state?.code; // если передаёшь code из предыдущего шага

    const form = useForm<IFormValues>({
        defaultValues: {
            newPassword: "",
            confirmPassword: "",
        },
    });

    const {
        watch,
    } = form;

    const newPasswordValue = watch("newPassword");

    // Защита: если email или code отсутствуют, редирект назад
    useEffect(() => {
        if (!email || !code) {
            navigate("/reset-password");
        }
    }, [email, code, navigate]);

    const onSubmit = async (data: IFormValues) => {
        if (!email || !code) return;

        const payload = {
            email,
            code,
            newPassword: data.newPassword,
            confirmPassword: data.confirmPassword,
        };

        const { meta } = await dispatch(ResetForgotPassword(payload));

        if (meta.requestStatus !== ERequestStatus.FULFILLED) {
            return;
        }

        navigate("/login");
    };

    return (
        <Box
            component="main"
            sx={{
                height: "100vh",
                backgroundImage: `url(${require("assets/images/auth_bg.jpg")})`,
                backgroundSize: "cover",
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
            }}
        >
            <Card
                sx={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    p: { xs: 0, sm: 2 },
                    width: { xs: "95%", md: "40%", lg: "35%", xl: "25%" },
                }}
            >
                <CardContent
                    sx={{
                        p: "20px",
                        py: "100px",
                        width: "100%",
                        paddingTop: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "inline-flex",
                            width: "100%",
                            justifyContent: "center",
                            height: 70,
                            mb: 2,
                        }}
                    >
                        <img src={Logo} alt="" />
                    </Box>

                    <Stack spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="h6">
                            Create new password
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Please enter your new password below.
                        </Typography>
                    </Stack>

                    <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
                        <FormProvider {...form}>
                            <Stack spacing={2}>
                                <TextInput<IFormValues>
                                    label="New Password"
                                    name="newPassword"
                                    type="password"
                                    rules={{
                                        ...requiredRules,
                                        minLength: {
                                            value: 6,
                                            message: "Password must be at least 6 characters",
                                        },
                                        validate: (value: string) => {
                                            const uppercase = /[A-Z]/.test(value);
                                            const number = /[0-9]/.test(value);
                                            const special = /[!@#$%^&*(),.?":{}|<>]/.test(value);

                                            if (!uppercase) return "Password must include at least one uppercase letter";
                                            if (!number) return "Password must include at least one number";
                                            if (!special) return "Password must include at least one special character";

                                            return true;
                                        },
                                    }}
                                />

                                <TextInput<IFormValues>
                                    label="Repeat Password"
                                    name="confirmPassword"
                                    type="password"
                                    rules={{
                                        ...requiredRules,
                                        validate: (value: string) =>
                                            value === watch("newPassword") || "Passwords do not match",
                                    }}
                                />
                            </Stack>

                            <Button
                                fullWidth
                                size="large"
                                sx={{ mt: 3 }}
                                type="submit"
                                variant="contained"
                            >
                                Save Password
                            </Button>
                        </FormProvider>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default NewPasswordPage;
